import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Pager } from '@/components/pager'
import { Panel } from '@/components/panel'
import { ScoreMeter } from '@/components/score-meter'
import { nextDirection, SortButton } from '@/components/sort-button'
import {
  type CompetitorDomainRow,
  type CompetitorDomainSort,
  type CompetitorDomainTable,
  DEFAULT_COMPETITOR_TABLE,
  type SharedCut,
  useCompetitorDomains,
} from '@/lib/competitors'
import { bareUrl, formatCount, formatDate, formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { FilterInput, TableSkeleton, useSiteScope } from '../../-section'

const PAGE_SIZE = 25

const CUTS: { key: SharedCut; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'gap', label: 'Not yours' },
  { key: 'shared', label: 'Shared' },
]

/** Widths are tight on purpose: seven columns on a page that already gives a
 * sidebar 52 rem left the domain — the one value a reader is actually scanning
 * — clipping on anything longer than a short name. Every fixed column is now
 * the width of its widest real value and no more. */
const COLUMNS: { key: CompetitorDomainSort; label: string; width: string }[] = [
  { key: 'backlinks', label: 'Links', width: 'w-[74px]' },
  { key: 'rank', label: 'Authority', width: 'w-[104px]' },
  { key: 'spamScore', label: 'Spam', width: 'w-[92px]' },
  { key: 'firstSeen', label: 'First seen', width: 'w-[96px]' },
]

export const Route = createFileRoute('/_app/$org/$host/competitors/_list/$competitorId')({
  component: CompetitorPage,
})

/**
 * One competitor's link profile, read against this site's.
 *
 * It opens on everything they have, because the nav entry that led here is
 * labelled with their domain and counted with their total — a page that opened
 * on a subset would disagree with the number that was clicked. The gap across
 * every competitor has its own entry above; the cut here is for narrowing this
 * one profile once you are in it.
 *
 * The ordering is by authority and cannot be re-sorted, for the reason the
 * gap's cannot: what is worth reading first is what is worth having first, and
 * that is a property of the domain rather than of the column a reader last
 * clicked.
 */
function CompetitorPage() {
  const { org, host, site } = useSiteScope()
  const { competitorId } = Route.useParams()
  const [table, setTable] = useState<CompetitorDomainTable>(DEFAULT_COMPETITOR_TABLE)
  const domains = useCompetitorDomains(site, competitorId, table, PAGE_SIZE)

  function sortBy(key: CompetitorDomainSort) {
    setTable((prev) => ({
      ...prev,
      sort: key,
      direction: nextDirection(prev.sort === key, prev.direction, key),
      offset: 0,
    }))
  }

  const competitor = domains.data?.competitor

  if (domains.isPending && !domains.data) {
    return (
      <>
        <h1 className="sr-only">Competitor</h1>
        <TableSkeleton />
      </>
    )
  }

  const total = domains.data?.total ?? 0

  return (
    <>
      <h1 className="sr-only">{competitor?.host ?? 'Competitor'}</h1>

      <Panel
        title={competitor?.host ?? 'Referring domains'}
        meta={total > 0 ? formatExact(total) : undefined}
        flush
        className={cn(domains.isFetching && 'opacity-70 transition-opacity')}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-3 py-2">
          <div className="inline-flex h-8 items-center rounded-md border border-border bg-card p-0.5">
            {CUTS.map((option) => (
              <button
                key={option.key}
                type="button"
                aria-pressed={table.cut === option.key}
                onClick={() =>
                  // A different cut is a different question, not page three of
                  // the last one.
                  setTable((prev) => ({ ...prev, cut: option.key, offset: 0 }))
                }
                className={cn(
                  'cursor-pointer rounded-[4px] px-2.5 py-1 text-[12.5px] transition-colors',
                  table.cut === option.key
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {(total > 0 || table.search) && (
            <FilterInput
              placeholder="Filter domains"
              value={table.search}
              onChange={(next) => setTable((prev) => ({ ...prev, search: next, offset: 0 }))}
              className="ml-auto"
            />
          )}
        </div>

        {domains.data && domains.data.rows.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="w-9 py-2 pr-2 pl-3 text-right">
                      <span className="mono-label text-muted-foreground/70">#</span>
                    </th>
                    <th className="w-[26%] py-2 pr-3 text-left">
                      <SortButton
                        label="Domain"
                        active={table.sort === 'domain'}
                        direction={table.direction}
                        onClick={() => sortBy('domain')}
                      />
                    </th>
                    <th className="w-[24%] py-2 pr-3 text-left">
                      <span className="mono-label text-muted-foreground">Example link</span>
                    </th>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        className={cn('px-3 py-2 text-right', column.width)}
                        aria-sort={
                          table.sort === column.key
                            ? table.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                        }
                      >
                        <SortButton
                          label={column.label}
                          active={table.sort === column.key}
                          direction={table.direction}
                          onClick={() => sortBy(column.key)}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {domains.data.rows.map((row, index) => (
                    <Row
                      key={row.id}
                      row={row}
                      rank={table.offset + index + 1}
                      org={org}
                      host={host}
                      showShared={table.cut === 'all'}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end border-t border-border px-3 py-2">
              <Pager
                offset={table.offset}
                limit={PAGE_SIZE}
                total={total}
                onOffsetChange={(next) => setTable((prev) => ({ ...prev, offset: next }))}
              />
            </div>
          </>
        ) : (
          <EmptyState
            title={competitor?.lastSyncedAt ? 'Nothing here' : 'Collecting their links'}
            description={
              competitor?.lastSyncedAt
                ? table.search
                  ? 'No domain matches that filter.'
                  : table.cut === 'gap'
                    ? `Every domain linking to ${competitor.host} already links to ${host}.`
                    : table.cut === 'shared'
                      ? `No domain links to both ${competitor.host} and ${host}.`
                      : `No links have been collected for ${competitor.host}.`
                : 'Their profile is being read. The first domains usually appear within a few minutes.'
            }
          />
        )}
      </Panel>
    </>
  )
}

function Row({
  row,
  rank,
  org,
  host,
  showShared,
}: {
  row: CompetitorDomainRow
  rank: number
  org: string
  host: string
  /** Only on the uncut list, where a row's status is the thing the reader
   * cannot infer from which cut they are on. */
  showShared: boolean
}) {
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-secondary/40">
      <td className="py-2 pr-2 pl-3 text-right align-middle">
        <span className="font-mono text-[11px] text-muted-foreground/70 tabular">
          {String(rank).padStart(2, '0')}
        </span>
      </td>

      <td className="max-w-0 py-2 pr-3">
        <div className="flex min-w-0 items-center gap-2">
          <Favicon host={row.domain} size={14} className="shrink-0 rounded-[3px]" />
          {/* A domain we already have opens ours rather than theirs: the
              question about a shared link is what it points at on our side. */}
          {row.shared ? (
            <Link
              to="/$org/$host/backlinks/$domainId"
              params={{ org, host, domainId: String(row.id) }}
              className="min-w-0 truncate font-mono text-[12.5px] underline-offset-2 hover:underline"
              title={row.domain}
            >
              {row.domain}
            </Link>
          ) : (
            <a
              href={`https://${row.domain}`}
              target="_blank"
              rel="noreferrer noopener"
              className="min-w-0 truncate font-mono text-[12.5px] underline-offset-2 hover:underline"
              title={row.domain}
            >
              {row.domain}
            </a>
          )}
          {showShared && row.shared && (
            <span className="mono-label shrink-0 text-muted-foreground/70">yours too</span>
          )}
        </div>
      </td>

      <td className="max-w-0 py-2 pr-3">
        {row.exampleUrlFrom ? (
          <div className="min-w-0">
            <a
              href={row.exampleUrlFrom}
              target="_blank"
              rel="noreferrer noopener"
              className="block truncate font-mono text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              title={row.exampleUrlFrom}
            >
              {bareUrl(row.exampleUrlFrom)}
            </a>
            {row.exampleAnchor && (
              <span
                className="block truncate text-[11.5px] text-muted-foreground/70"
                title={row.exampleAnchor}
              >
                “{row.exampleAnchor}”
              </span>
            )}
          </div>
        ) : (
          <span className="font-mono text-[11.5px] text-muted-foreground/50">—</span>
        )}
      </td>

      <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
        <span className="font-mono text-[12px] tabular" title={formatExact(row.backlinks)}>
          {formatCount(row.backlinks)}
        </span>
      </td>

      <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
        <ScoreMeter kind="authority" value={row.rank} />
      </td>

      <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
        <ScoreMeter kind="spam" value={row.spamScore} />
      </td>

      <td className="px-3 py-2 text-right align-middle whitespace-nowrap">
        <span className="font-mono text-[11.5px] text-muted-foreground tabular">
          {row.firstSeen ? formatDate(row.firstSeen) : '—'}
        </span>
      </td>
    </tr>
  )
}
